import rg4js from "raygun4js";
import { useCallback, useMemo, useRef, useState } from "react";
import type { ZipUrl } from "@/briefpoint-client/models";
import { toast } from "@/components/ui/toast";
import { normalizeError } from "@/lib/utils";

export function useDownloadZipFilesWithProgress(
  options?: DownloadZipFilesWithProgressOptions,
): DownloadZipFilesWithProgressResult {
  const onErrorRef = useRef(options?.onError);
  onErrorRef.current = options?.onError;

  const [progress, setProgress] = useState<ZipDownloadProgressState | null>(
    null,
  );

  const downloadZipFiles = useCallback(async (zipUrls: ZipUrl[]) => {
    const valid = filterValidZipUrls(zipUrls);
    if (valid.length === 0) {
      return;
    }

    setProgress(buildInitialProgress(valid));

    const handleError = (error: Error, fileName: string) => {
      const override = onErrorRef.current;
      if (override) {
        override(error, fileName);
      } else {
        defaultZipDownloadErrorHandler(error, fileName);
      }
    };

    try {
      await runSequentialZipDownloads(valid, (loaded, total, context) => {
        setProgress(progressStateFromXhr(loaded, total, context));
      }, handleError);
    } finally {
      setProgress(null);
    }
  }, []);

  const derived = useMemo(
    () => (progress ? deriveProgressSlice(progress) : IDLE_DERIVED),
    [progress],
  );

  return {
    downloadZipFiles,
    isDownloading: progress !== null,
    progress,
    ...derived,
  };
}

const MB = 1024 * 1024;

function defaultZipDownloadErrorHandler(
  error: Error,
  fileName: string,
) {
  rg4js("send", {
    error,
  });
  toast.error(`Error downloading ${fileName}`);
}

function formatMb(bytes: number, fractionDigits = 1): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return "0.0";
  }
  return (bytes / MB).toFixed(fractionDigits);
}

type ZipDownloadProgressState = {
  loadedBytes: number;
  totalBytes: number | null;
  fileIndex: number;
  fileCount: number;
  fileName: string;
};

type DownloadZipFilesWithProgressOptions = {
  onError?: (error: Error, fileName: string) => void;
};

type DownloadZipFilesWithProgressResult = {
  downloadZipFiles: (zipUrls: ZipUrl[]) => Promise<void>;
  isDownloading: boolean;
  progress: ZipDownloadProgressState | null;
  loadedBytes: number | null;
  totalBytes: number | null;
  progressPercent: number | null;
  progressMbString: string;
  loadedMb: number | null;
  totalMb: number | null;
  fileIndexDisplay: number | null;
  fileCount: number | null;
  currentFileName: string | null;
};

type ValidZipEntry = { url: string; fileName: string };

type XhrProgressContext = {
  fileIndex: number;
  fileCount: number;
  fileName: string;
};

type DerivedProgressSlice = Omit<
  DownloadZipFilesWithProgressResult,
  "downloadZipFiles" | "isDownloading" | "progress"
>;

const IDLE_DERIVED: DerivedProgressSlice = {
  loadedBytes: null,
  totalBytes: null,
  progressPercent: null,
  progressMbString: "",
  loadedMb: null,
  totalMb: null,
  fileIndexDisplay: null,
  fileCount: null,
  currentFileName: null,
};

function filterValidZipUrls(zipUrls: ZipUrl[]): ValidZipEntry[] {
  return zipUrls.filter(
    (z): z is ValidZipEntry => Boolean(z.url && z.fileName),
  );
}

function buildInitialProgress(valid: ValidZipEntry[]): ZipDownloadProgressState {
  const first = valid[0];
  return {
    loadedBytes: 0,
    totalBytes: null,
    fileIndex: 0,
    fileCount: valid.length,
    fileName: first?.fileName ?? "",
  };
}

function progressStateFromXhr(
  loaded: number,
  total: number | undefined,
  context: XhrProgressContext,
): ZipDownloadProgressState {
  return {
    loadedBytes: loaded,
    totalBytes: total === undefined ? null : total,
    fileIndex: context.fileIndex,
    fileCount: context.fileCount,
    fileName: context.fileName,
  };
}

function deriveProgressSlice(
  progress: ZipDownloadProgressState,
): DerivedProgressSlice {
  const { loadedBytes, totalBytes, fileIndex, fileCount, fileName } = progress;
  const loadedMb = loadedBytes / MB;
  const totalMb = totalBytes !== null ? totalBytes / MB : null;
  const progressPercent =
    totalBytes !== null && totalBytes > 0
      ? Math.min(100, (loadedBytes / totalBytes) * 100)
      : null;
  const progressMbString =
    totalBytes !== null
      ? `${formatMb(loadedBytes)} / ${formatMb(totalBytes)} MB`
      : `${formatMb(loadedBytes)} MB`;

  return {
    loadedBytes,
    totalBytes,
    progressPercent,
    progressMbString,
    loadedMb,
    totalMb,
    fileIndexDisplay: fileIndex + 1,
    fileCount,
    currentFileName: fileName,
  };
}

function downloadUrlAsBlobViaXhr(
  url: string,
  onProgress: (loaded: number, total: number | undefined) => void,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url);
    xhr.responseType = "blob";
    try {
      xhr.setRequestHeader("Accept-Encoding", "identity");
    } catch {
      /* some environments forbid this header; progress may still use wire-length */
    }
    xhr.onprogress = (ev) => {
      const total = ev.lengthComputable ? ev.total : undefined;
      onProgress(ev.loaded, total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const blob = xhr.response as Blob;
        onProgress(blob.size, blob.size);
        resolve(blob);
        return;
      }
      reject(new Error(`Download failed (${xhr.status})`));
    };
    xhr.onerror = () => {
      reject(new Error("Network error while downloading"));
    };
    xhr.onabort = () => {
      reject(new Error("Download aborted"));
    };
    xhr.send();
  });
}

function triggerBlobDownloadForZip(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(blob);
  const link = window.document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(url);
}

async function runSequentialZipDownloads(
  valid: ValidZipEntry[],
  onProgress: (
    loaded: number,
    total: number | undefined,
    context: XhrProgressContext,
  ) => void,
  onError?: (error: Error, fileName: string) => void,
): Promise<void> {
  for (let i = 0; i < valid.length; i += 1) {
    const zipUrl = valid[i];
    try {
      const blob = await downloadUrlAsBlobViaXhr(zipUrl.url, (loaded, total) => {
        onProgress(loaded, total, {
          fileIndex: i,
          fileCount: valid.length,
          fileName: zipUrl.fileName,
        });
      });
      triggerBlobDownloadForZip(blob, zipUrl.fileName);
    } catch (error) {
      const normalizedError = normalizeError(error as Error);
      if (onError) {
        onError(normalizedError, zipUrl.fileName);
      } else {
        console.error(`Error downloading ${zipUrl.fileName}:`, normalizedError);
      }
    }
  }
}
